import React from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  ArrowBack,
  Download,
  Collections,
} from '@mui/icons-material';
import { issuesAPI, pagesAPI } from '../services/api';
import PDFViewer from '../components/PDFViewer/PDFViewer';
import { colors } from '../theme';

const IssueDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const initialPage = searchParams.get('page') ? parseInt(searchParams.get('page'), 10) : 1;

  const { data, isLoading, error } = useQuery(
    ['issue', id],
    () => issuesAPI.getById(id),
    { enabled: !!id }
  );

  // Fetch pages separately - lightweight call for page images only
  const { data: pagesData } = useQuery(
    ['issue-pages', id],
    () => pagesAPI.getAll({ issue: id, page_size: 500 }),
    { enabled: !!id }
  );

  const issue = data?.data;
  const pages = pagesData?.data?.results || [];

  const handleDownload = async () => {
    try {
      const response = await issuesAPI.download(id);
      const downloadUrl = response.data.pdf_url;
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = response.data.filename;
      link.click();
    } catch (error) {
      console.error('Download error:', error);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !issue) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{t('errorLoadingIssues')}</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ backgroundColor: colors.lightGray, minHeight: '100vh', py: 4 }}>
      <Container maxWidth="xl">
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate(-1)}
            sx={{ cursor: 'pointer' }}
          >
            {t('home')}
          </Link>
          <Typography variant="body2" color="text.primary">
            {i18n.language === 'zh' ? issue.title_zh : issue.title_en}
          </Typography>
        </Breadcrumbs>

        {/* Back Button */}
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          {t('previous')}
        </Button>

        <Grid container spacing={3}>
          {/* Sidebar - Issue Info */}
          <Grid item xs={12} md={3}>
            <Paper elevation={2} sx={{ p: 3, position: 'sticky', top: 80 }}>
              {/* Cover Image */}
              {issue.cover_image && (
                <Box
                  component="img"
                  src={issue.cover_image}
                  alt={i18n.language === 'zh' ? issue.title_zh : issue.title_en}
                  sx={{
                    width: '100%',
                    borderRadius: 1,
                    mb: 2,
                  }}
                />
              )}

              {/* Title */}
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                {i18n.language === 'zh' ? issue.title_zh : issue.title_en}
              </Typography>

              {/* Publication Info */}
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('publicationYear')}: {issue.publication_year}
                {issue.publication_month && ` / ${issue.publication_month}`}
              </Typography>

              {issue.volume_number && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('volumeNumber')}: {issue.volume_number}
                </Typography>
              )}

              {issue.issue_number && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('issueNumber')}: {issue.issue_number}
                </Typography>
              )}

              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('totalPages')}: {issue.total_pages}
              </Typography>

              {/* Description */}
              {(issue.description_zh || issue.description_en) && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {i18n.language === 'zh' ? issue.description_zh : issue.description_en}
                  </Typography>
                </Box>
              )}

              {/* Tags */}
              {issue.tags && issue.tags.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('filterByTags')}:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {issue.tags.map((tag) => (
                      <Chip
                        key={tag.id}
                        label={i18n.language === 'zh' ? tag.name_zh : tag.name_en}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Actions */}
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<Download />}
                  onClick={handleDownload}
                  sx={{
                    backgroundColor: colors.lionsGold,
                    color: colors.royalBlue,
                    fontWeight: 600,
                    mb: 1,
                    '&:hover': {
                      backgroundColor: '#FFCA28',
                    },
                  }}
                >
                  {t('downloadIssue')}
                </Button>

                {issue.photo_count > 0 && (
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<Collections />}
                    onClick={() => navigate(`/photos?issue=${id}`)}
                  >
                    {t('relatedPhotos')} ({issue.photo_count})
                  </Button>
                )}
              </Box>

              {/* Processing Status */}
              {issue.processing_status === 'processing' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {t('processing')}: {issue.processing_progress}%
                </Alert>
              )}

              {issue.processing_status === 'failed' && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {t('errorProcessing')}
                </Alert>
              )}
            </Paper>
          </Grid>

          {/* Main Content - PDF Viewer */}
          <Grid item xs={12} md={9}>
            <Paper elevation={2} sx={{ p: 3 }}>
              {pages.length > 0 ? (
                <PDFViewer
                  pdfUrl={issue.pdf_file}
                  issueTitle={i18n.language === 'zh' ? issue.title_zh : issue.title_en}
                  initialPage={initialPage}
                  pages={pages}
                  totalPages={issue.total_pages}
                />
              ) : issue.pdf_file ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                  <Typography sx={{ ml: 2 }}>{t('loading')}...</Typography>
                </Box>
              ) : (
                <Alert severity="warning">{t('errorLoadingPages')}</Alert>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default IssueDetailPage;
